// Iter 91n — Non-dismissable "change your password" modal shown on the
// first login after an admin provisioned the account with a temporary
// password (`must_change_password: true`).  Closes only after the backend
// confirms the change and clears the flag.

import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Save, Loader2, Eye, EyeOff, ShieldAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const MIN_LEN = 8;

export function ForcedPasswordChangeDialog() {
  const { user, checkAuth } = useAuth();
  const [cur,   setCur]   = useState("");
  const [next,  setNext]  = useState("");
  const [conf,  setConf]  = useState("");
  const [show,  setShow]  = useState(false);
  const [busy,  setBusy]  = useState(false);

  // Only render when the backend says so — no local dismiss. Never while an
  // admin is masquerading: viewing as someone must not force (or let) you set
  // THEIR password — they'll still be prompted on their own real login.
  const open = !!user?.must_change_password && !user?.masqueraded_by;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (next.length < MIN_LEN)  return toast.error(`New password must be at least ${MIN_LEN} characters.`);
    if (next !== conf)          return toast.error("Passwords do not match.");
    if (next === cur)           return toast.error("Choose a password different from the temporary one.");
    try {
      setBusy(true);
      await api.post("/auth/change-my-password", {
        current_password: cur, new_password: next,
      });
      toast.success("Password updated.  Welcome aboard.");
      await checkAuth?.();   // pulls fresh /auth/me so must_change_password flips to false
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update password.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open}>
      {/* onOpenChange intentionally omitted — Escape/click-outside must not close */}
      <DialogContent className="max-w-md [&>button.absolute]:hidden" data-testid="forced-password-change-dialog"
                     onEscapeKeyDown={(e) => e.preventDefault()}
                     onPointerDownOutside={(e) => e.preventDefault()}
                     onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-primary" />
            Set your new password
          </DialogTitle>
          <DialogDescription>
            Your account was created with a temporary password.  Please choose a
            new one now to continue.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 flex items-start gap-2">
          <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div>Signed in as <b>{user?.email}</b>.  This modal cannot be dismissed
          until a new password is saved.</div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Temporary password</Label>
            <div className="relative">
              <Input type={show ? "text" : "password"} value={cur}
                     onChange={(e) => setCur(e.target.value)}
                     autoFocus autoComplete="current-password"
                     data-testid="forced-pw-current" />
              <button type="button" onClick={() => setShow((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}>
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label>New password</Label>
            <Input type={show ? "text" : "password"} value={next}
                   onChange={(e) => setNext(e.target.value)}
                   autoComplete="new-password" minLength={MIN_LEN}
                   data-testid="forced-pw-new" />
            <div className="mt-1 text-[10px] text-slate-400">
              At least {MIN_LEN} characters.
            </div>
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input type={show ? "text" : "password"} value={conf}
                   onChange={(e) => setConf(e.target.value)}
                   autoComplete="new-password" minLength={MIN_LEN}
                   data-testid="forced-pw-confirm" />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={busy || !cur || !next || !conf}
                    data-testid="forced-pw-save" className="gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save & continue
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
